import { httpClient } from '@/services/http/httpClient';
import { env } from '@/lib/env';
import { billingApiMock } from '@/services/api/billingApi.mock';
import { isApiError } from '@/types';
import type { PlanCard, SubscriptionState, UsageSummary, Invoice, PaymentMethod } from '@/types';

/**
 * Billing API client — wraps the 7 `/api/billing` endpoints (architecture §7).
 * httpClient auto-unwraps the `{ data, meta, timestamp }` envelope, so each
 * method resolves directly to the inner `data` payload.
 *
 * Read endpoints auto-fall back to the contract-shaped fixtures in
 * `billingApi.mock.ts` when the BE route is still undeployed (404). This is
 * DEV-ONLY and switches to the real BE automatically once the route ships — see
 * `resolveBillingApi` below. For fully offline work, set VITE_ENABLE_MOCK_API=true.
 */
const billingApiReal = {
  /** 1. GET /billing/catalog */
  getCatalog: () => httpClient.get<{ plans: PlanCard[] }>('/billing/catalog'),

  /** 2. GET /billing/subscription */
  getSubscription: () => httpClient.get<SubscriptionState>('/billing/subscription'),

  /** 3. GET /billing/usage */
  getUsage: () => httpClient.get<UsageSummary>('/billing/usage'),

  /** 4. GET /billing/invoices */
  getInvoices: () => httpClient.get<{ invoices: Invoice[]; hasMore: boolean }>('/billing/invoices'),

  /** 5. GET /billing/payment-method */
  getPaymentMethod: () => httpClient.get<PaymentMethod | null>('/billing/payment-method'),

  /** 6. POST /billing/checkout — returns a Stripe-hosted Checkout URL. */
  checkout: (planId: string) => httpClient.post<{ url: string }>('/billing/checkout', { planId }),

  /** 7. POST /billing/portal — returns a Stripe Billing Portal URL. */
  openPortal: () => httpClient.post<{ url: string }>('/billing/portal', {}),
};

export type BillingApi = typeof billingApiReal;

/**
 * A 404 means the BE route isn't deployed yet — deliberately narrow. We do NOT
 * treat 401/403 (auth / not entitled), 5xx (outage) or network errors as
 * "missing": masking those with a fixture would hide real failures and, worse,
 * could fabricate an active subscription for a request the BE actually rejected.
 * Detected from the unwrapped error envelope (`error.code === 'NOT_FOUND'`) or
 * the raw axios status when the envelope is absent.
 */
function isEndpointMissing(err: unknown): boolean {
  if (isApiError(err) && err.error.code === 'NOT_FOUND') return true;
  const status = (err as { response?: { status?: number }; status?: number } | null | undefined)
    ?.response?.status;
  return status === 404;
}

/**
 * Wrap a real read so it serves the mock fixture ONLY on a 404 (route not
 * deployed). Any other error propagates unchanged. Once the BE ships the route
 * it returns 200 and the real payload flows through — no flag flip, no redeploy.
 */
function withMockFallback<T>(
  real: () => Promise<T>,
  mock: () => Promise<T>,
  route: string
): () => Promise<T> {
  return async () => {
    try {
      return await real();
    } catch (err) {
      if (isEndpointMissing(err)) {
        console.warn(
          `[billing] ${route} not deployed (404) — serving mock fixture (dev only). ` +
            `Auto-switches to the BE once the route is live.`
        );
        return mock();
      }
      throw err;
    }
  };
}

function resolveBillingApi(): BillingApi {
  // SECURITY BOUNDARY: every mock path lives inside this `import.meta.env.DEV`
  // block. In a production/staging build `import.meta.env.DEV` is statically
  // `false`, so the whole block — and with it the `billingApiMock` import and
  // all fixtures — is dead-code eliminated from the bundle. Mock billing data
  // must never reach a deployed environment: a fabricated "active" subscription
  // would let an unpaid user past `SubscriptionGate`. Deployed builds always
  // use the real BE and surface real 404/401/5xx.
  if (import.meta.env.DEV) {
    // 1. Explicit full mock (fully offline dev). With BILLING_LIVE_CHECKOUT on,
    //    reads stay mocked but checkout/portal hit the real BE (real Stripe).
    if (env.ENABLE_MOCK_API) {
      return env.BILLING_LIVE_CHECKOUT
        ? {
            ...billingApiMock,
            checkout: billingApiReal.checkout,
            openPortal: billingApiReal.openPortal,
          }
        : billingApiMock;
    }

    // 2. Auto-fallback: hit the real BE first, serve a fixture only on a 404
    //    (route not deployed yet). Switches to the real payload automatically
    //    once the BE ships the route — no flag flip, no redeploy. checkout/portal
    //    are NOT wrapped: a mock URL must never stand in for real Stripe.
    if (env.BILLING_MOCK_FALLBACK) {
      return {
        ...billingApiReal,
        getCatalog: withMockFallback(
          billingApiReal.getCatalog,
          billingApiMock.getCatalog,
          'GET /billing/catalog'
        ),
        getSubscription: withMockFallback(
          billingApiReal.getSubscription,
          billingApiMock.getSubscription,
          'GET /billing/subscription'
        ),
        getUsage: withMockFallback(
          billingApiReal.getUsage,
          billingApiMock.getUsage,
          'GET /billing/usage'
        ),
        getInvoices: withMockFallback(
          billingApiReal.getInvoices,
          billingApiMock.getInvoices,
          'GET /billing/invoices'
        ),
        getPaymentMethod: withMockFallback(
          billingApiReal.getPaymentMethod,
          billingApiMock.getPaymentMethod,
          'GET /billing/payment-method'
        ),
      };
    }
  }

  // 3. Production / staging (and dev with both mock modes off): real BE only.
  return billingApiReal;
}

export const billingApi: BillingApi = resolveBillingApi();
